import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState } from "react";
import { toast } from "sonner";

type DataParams = {
  rotationId?: string;
  from?: string;
  to?: string;
  referencedUsersOnly?: boolean;
};

type ExplorationsOnDateParams = {
  date: string;
  sessionNames?: string;
};

type UpcomingParams = {
  start?: string;
  end?: string;
};

type LoadingMap = {
  data: boolean;
  explorationsOnDate: boolean;
  quarterlySummary: boolean;
  upcoming: boolean;
};

const buildQuery = (params: Record<string, string | undefined>) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, v);
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
};

export const useExports = () => {
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState<LoadingMap>({
    data: false,
    explorationsOnDate: false,
    quarterlySummary: false,
    upcoming: false,
  });

  const wrap = async <K extends keyof LoadingMap>(
    key: K,
    label: string,
    work: () => Promise<unknown>,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, [key]: true }));
      await work();
      toast.success(`${label} downloaded`);
    } catch (error: any) {
      toast.error(error?.message ?? `Failed to download ${label.toLowerCase()}`);
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const downloadData = (params: DataParams) =>
    wrap("data", "Full data export", () =>
      api(
        `/exports/data${buildQuery({
          rotationId: params.rotationId,
          from: params.from,
          to: params.to,
          referencedUsersOnly: params.referencedUsersOnly ? "1" : undefined,
        })}`,
        { method: "GET", responseType: "file" },
        dispatch,
      ),
    );

  const downloadExplorationsOnDate = (params: ExplorationsOnDateParams) =>
    wrap("explorationsOnDate", "Explorations on date", () =>
      api(
        `/exports/explorations-on-date${buildQuery({
          date: params.date,
          sessionNames: params.sessionNames,
        })}`,
        { method: "GET", responseType: "file" },
        dispatch,
      ),
    );

  const downloadQuarterlySummary = () =>
    wrap("quarterlySummary", "Quarterly summary", () =>
      api(
        "/exports/quarterly-summary",
        { method: "GET", responseType: "file" },
        dispatch,
      ),
    );

  const downloadUpcomingExplorations = (params: UpcomingParams) =>
    wrap("upcoming", "Upcoming explorations", () =>
      api(
        `/exports/upcoming-explorations${buildQuery({
          start: params.start,
          end: params.end,
        })}`,
        { method: "GET", responseType: "file" },
        dispatch,
      ),
    );

  return {
    loading,
    downloadData,
    downloadExplorationsOnDate,
    downloadQuarterlySummary,
    downloadUpcomingExplorations,
  };
};
